import React, { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { ScrollText, ExternalLink } from "lucide-react";

interface PMSLog {
  Time: string;
  LogLevel: string;
  Connector: string;
  ConnectorHotelId: string;
  HotelCode: string;
  SaasId: string;
  Ref: string;
  Method: string;
  Action: string;
  SizeBytes: string;
  Datapoints: string;
  ResponseTime: string;
  TraceFile: string;
  StatusCode: string;
  Status: string;
}

export function SaasPMSLogs() {
  const [logs, setLogs] = useState<PMSLog[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("3hours");
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [displayDateRange, setDisplayDateRange] =
    useState<string>("Last 3 hours");

  useEffect(() => {
    loadLogs();

    // Update the display text when dateRange changes
    if (dateRange.startsWith("custom_")) {
      const [_, startDateTime, endDateTime] = dateRange.split("_");
      const formattedStart = formatDateForDisplay(startDateTime);
      const formattedEnd = formatDateForDisplay(endDateTime);
      setDisplayDateRange(`Custom: ${formattedStart} - ${formattedEnd}`);
    } else {
      // Set display text for predefined ranges
      const rangeDisplayMap: Record<string, string> = {
        "30min": "Last 30 minutes",
        "60min": "Last 60 minutes",
        "3hours": "Last 3 hours",
        "6hours": "Last 6 hours",
        "12hours": "Last 12 hours",
        "24hours": "Last 24 hours",
        "3days": "Last 3 days",
        "7days": "Last 7 days",
        custom: "Custom date range",
      };
      setDisplayDateRange(rangeDisplayMap[dateRange] || dateRange);
    }
  }, [dateRange]);

  // Helper function to format date for display
  const formatDateForDisplay = (dateTimeString: string): string => {
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return dateTimeString;
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setError("User not authenticated");
        return;
      }

      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        setError("No access token available");
        return;
      }

      // Use the authenticated user's ID as the saasId
      const saasId = user.id;

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

      // Build the URL based on whether it's a custom date range or predefined range
      let url = `${apiBaseUrl}/api/newrelic/logs/pms?saasId=${saasId}`;

      if (dateRange.startsWith("custom_")) {
        // Extract start and end dates from the custom format
        const [_, startDateTime, endDateTime] = dateRange.split("_");
        url += `&startDate=${encodeURIComponent(
          startDateTime
        )}&endDate=${encodeURIComponent(endDateTime)}`;
      } else if (dateRange === "custom") {
        // If "custom" is selected but no dates are submitted yet, use a default range
        // This shouldn't happen with our updated UI flow, but adding as a safeguard
        setError("Please select and apply a custom date range");
        return; // Exit the function to prevent the API call
      } else {
        url += `&dateRange=${dateRange}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();
      setLogs(data || []);
    } catch (err) {
      setError("Failed to load PMS logs");
      console.error("Error loading logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    if (value === "custom") {
      // If custom option is selected, either:
      // 1. Show date picker with empty dates if no custom range was previously set
      // 2. Show date picker with the current custom dates if a custom range was previously set
      if (dateRange.startsWith("custom_")) {
        const [_, startDateTime, endDateTime] = dateRange.split("_");
        setStartDate(startDateTime);
        setEndDate(endDateTime);
      }
      setShowCustomDatePicker(true);
      // Don't update dateRange yet - we'll do that when the user submits the custom dates
    } else {
      // For standard date ranges, update immediately and close the picker
      setDateRange(value);
      setShowCustomDatePicker(false);
    }
  };

  const handleCustomDateSubmit = () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      return;
    }

    // Clear any previous errors
    setError("");

    // Format: custom_YYYY-MM-DDTHH:MM:SS_YYYY-MM-DDTHH:MM:SS
    const formattedDateRange = `custom_${startDate}_${endDate}`;
    setDateRange(formattedDateRange);

    // Update display text immediately
    const formattedStart = formatDateForDisplay(startDate);
    const formattedEnd = formatDateForDisplay(endDate);
    setDisplayDateRange(`Custom: ${formattedStart} - ${formattedEnd}`);

    setShowCustomDatePicker(false);

    // Trigger logs reload with the new date range
    loadLogs();
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="px-6 py-4 text-center text-sm text-gray-500">
            Loading PMS logs...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <ScrollText className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-bold text-gray-600">PMS Logs</h2>
          </div>
          <div className="flex items-center space-x-2">
            <label htmlFor="dateRange" className="text-sm text-gray-600">
              Time Range:
            </label>
            <div className="relative">
              <div className="border border-gray-300 rounded px-2 py-1 text-sm bg-white flex items-center min-w-[180px]">
                <div
                  className="w-full cursor-pointer"
                  onClick={() => {
                    if (dateRange.startsWith("custom_")) {
                      // If custom date range is selected, open the date picker
                      const [_, startDateTime, endDateTime] =
                        dateRange.split("_");
                      setStartDate(startDateTime);
                      setEndDate(endDateTime);
                      setShowCustomDatePicker(true);
                    }
                  }}
                >
                  <select
                    id="dateRange"
                    value={
                      dateRange.startsWith("custom_") ? "custom" : dateRange
                    }
                    onChange={handleDateRangeChange}
                    className="w-full bg-transparent border-none focus:outline-none"
                  >
                    <option value="30min">Last 30 minutes</option>
                    <option value="60min">Last 60 minutes</option>
                    <option value="3hours">Last 3 hours</option>
                    <option value="6hours">Last 6 hours</option>
                    <option value="12hours">Last 12 hours</option>
                    <option value="24hours">Last 24 hours</option>
                    <option value="3days">Last 3 days</option>
                    <option value="7days">Last 7 days</option>
                    <option value="custom">
                      {dateRange.startsWith("custom_")
                        ? displayDateRange
                        : "Custom date range"}
                    </option>
                  </select>
                </div>
              </div>

              {showCustomDatePicker && (
                <div className="absolute mt-2 p-4 bg-white shadow-lg rounded border border-gray-200 z-10 left-0 top-full w-64">
                  <div className="flex flex-col space-y-3">
                    <div className="flex flex-col">
                      <label
                        htmlFor="startDate"
                        className="text-sm text-gray-600 mb-1"
                      >
                        Start Date:
                      </label>
                      <input
                        type="datetime-local"
                        id="startDate"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label
                        htmlFor="endDate"
                        className="text-sm text-gray-600 mb-1"
                      >
                        End Date:
                      </label>
                      <input
                        type="datetime-local"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setShowCustomDatePicker(false)}
                        className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCustomDateSubmit}
                        className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => loadLogs()}
              className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Time</th>
                <th className="table-header">Hotel Code</th>
                <th className="table-header">Connector</th>
                <th className="table-header">Method</th>
                <th className="table-header">Action</th>
                <th className="table-header">Response Time</th>
                <th className="table-header">Size</th>
                <th className="table-header">Datapoints</th>
                <th className="table-header">Status</th>
                <th className="table-header">Trace</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {logs.map((log) => (
                <tr key={log.Ref} className="table-row">
                  <td className="table-cell">
                    {new Date(log.Time).toLocaleString()}
                  </td>
                  <td className="table-cell">{log.HotelCode}</td>
                  <td className="table-cell">{log.Connector}</td>
                  <td className="table-cell">
                    <span
                      className={`status-badge ${
                        log.Method === "GET"
                          ? "bg-blue-100"
                          : log.Method === "POST"
                          ? "bg-green-100"
                          : log.Method === "PUT"
                          ? "bg-yellow-100"
                          : "bg-red-100"
                      }`}
                    >
                      {log.Method}
                    </span>
                  </td>
                  <td className="table-cell">{log.Action}</td>
                  <td className="table-cell">{log.ResponseTime}</td>
                  <td className="table-cell">{log.SizeBytes}</td>
                  <td className="table-cell">{log.Datapoints}</td>
                  <td className="table-cell">
                    <span
                      className={`status-badge ${
                        log.StatusCode >= "200" && log.StatusCode < "300"
                          ? "status-active"
                          : "status-error"
                      }`}
                    >
                      {log.StatusCode} - {log.Status}
                    </span>
                  </td>
                  <td className="table-cell">
                    {log.TraceFile && (
                      <a
                        href={log.TraceFile}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-700 flex items-center"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Trace
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {logs.length === 0 && !error && (
            <div className="text-center py-8">
              <p className="px-6 py-4 text-center text-sm text-gray-500">
                No logs found. Logs will appear here when you make API calls to
                hotel PMS systems.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
